import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import "./Warehouse.css";

export default function Warehouse() {
  const { user, token, logout } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [warehouseName, setWarehouseName] = useState("");
  const [street, setStreet] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [storageSize, setStorageSize] = useState(0);

  const [mainWarehouseId, setMainWarehouseId] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [level1, setLevel1] = useState([]);
  const [level2, setLevel2] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const location = useLocation();

  useEffect(() => {
    if (!token) return; 
    fetchWarehouses();
  }, [token]);

  const fetchWarehouses = async (forceFetch = false) => {
    try {
      setLoading(true);
      setError("");

      // Try load cached warehouses first
      if (!forceFetch) {
        const cachedWarehouses = sessionStorage.getItem("warehouses");
        if (cachedWarehouses) {
          const parsed = JSON.parse(cachedWarehouses);
          setWarehouses(parsed);
          
          // Rebuild Level 1 and Level 2 using cached data
          const l1 = parsed.filter(w => !w.mainWarehouseId);
          const l2 = {};

          parsed.forEach(w => {
            if (w.mainWarehouseId) {
              if (!l2[w.mainWarehouseId]) l2[w.mainWarehouseId] = [];
              l2[w.mainWarehouseId].push(w);
            }
          });

          setLevel1(l1);
          setLevel2(l2);

          return;
        }
      }
    
      const response = await fetch(`${API_BASE_URL}/Warehouse`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch warehouses");

      const data = await response.json();
      setWarehouses(data);
      sessionStorage.setItem("warehouses", JSON.stringify(data));

      // Split into level 1 & level 2
      const l1 = data.filter(w => !w.mainWarehouseId);
      const l2 = {};

      data.forEach(w => {
        if (w.mainWarehouseId) {
          if (!l2[w.mainWarehouseId]) l2[w.mainWarehouseId] = [];
          l2[w.mainWarehouseId].push(w);
        }
      });

      setLevel1(l1);
      setLevel2(l2);
    } catch (err) {
      setError(err.message);
    }
    finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("add") === "true") {
      resetForm();
      setShowModal(true);
      window.history.replaceState({}, "", "/warehouses");
    }
  }, [location.search]);

  const resetForm = () => {
    setWarehouseName("");
    setStreet("");
    setSuburb("");
    setCity("");
    setPostalCode("");
    setCountry("");
    setStorageSize(0);
    setMainWarehouseId(null);
    setEditingWarehouse(null);
  };

  // Delay
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Retry wrapper
  const fetchWithRetry = async (url, options = {}, retries = 4, baseDelay = 800) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const res = await fetch(url, options);
        if (res.status === 429) {
          const jitter = Math.floor(Math.random() * 300);
          const wait = baseDelay * Math.pow(2, attempt - 1) + jitter;
          await delay(wait);
          continue;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res;
      } catch (err) {
        if (attempt === retries) throw err;
        const wait = baseDelay * Math.pow(2, attempt - 1);
        await delay(wait);
      }
    }
  };

  const handleCreateAndEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const locationParts = [street, suburb, city, postalCode, country].filter(
      (part) => part && part.trim() !== ""
    );
    const location = locationParts.join(", ");

    const warehouseData = {
      name: warehouseName,
      location,
      storageSize: parseFloat(storageSize),
      mainWarehouseId: mainWarehouseId || null
    };

    try {
      const method = editingWarehouse ? "PUT" : "POST";
      const url = editingWarehouse
        ? `${API_BASE_URL}/Warehouse/${editingWarehouse.id}`
        : `${API_BASE_URL}/Warehouse`;

      const response = await fetchWithRetry(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(warehouseData),
      });

      const saved = await response.json();

       

      
      sessionStorage.removeItem("warehouses");
      await fetchWarehouses(true);
      alert(editingWarehouse ? "Warehouse updated!" : "Warehouse added!");
      
      resetForm();
      setShowModal(false);
    } catch (err) {
      setError(err.message || "Failed to save warehouse.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWarehouse = async (id) => {
    if (!window.confirm("Are you sure you want to remove this warehouse?"))
      return;

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/Warehouse/${id}`, { 
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to delete warehouse");

      setWarehouses(prev => {
        const updated = prev.filter(w => w.id !== id);

        // Update sessionStorage
        sessionStorage.setItem("warehouses", JSON.stringify(updated));

        // Update Level1 & Level2
        const l1 = updated.filter(w => !w.mainWarehouseId);
        const l2 = {};
        updated.forEach(w => {
          if (w.mainWarehouseId) {
            if (!l2[w.mainWarehouseId]) l2[w.mainWarehouseId] = [];
            l2[w.mainWarehouseId].push(w);
          }
        });

        setLevel1(l1);
        setLevel2(l2);

        return updated;
      });
      await fetchWarehouses(true);
    } catch (err) {
      alert("Error deleting warehouse.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditWarehouse = (warehouse) => {
    const [street, suburb, city, postalCode, country] =
      warehouse.location?.split(",").map((part) => part.trim()) || [];

    setWarehouseName(warehouse.name);
    setStreet(street || "");
    setSuburb(suburb || "");
    setCity(city || "");
    setPostalCode(postalCode || "");
    setCountry(country || "");
    setStorageSize(warehouse.storageSize || 0);
    setMainWarehouseId(warehouse.mainWarehouseId || null);

    setEditingWarehouse(warehouse);
    setShowModal(true);
  };

  const getWarehouseNameById = (id) => {
    return warehouses.find(w => w.id === id).name;
  };

  const handleBackToDashboard = () => {
    window.location.href = '/dashboard';
  };

  const [filters, setFilters] = useState({
    search: "",
    minStorage: "",
    maxStorage: "",
    level: "",
    parentId: "",
  });

  const allWarehouses = [
    ...level1.map(parent => ({ ...parent, level: "1", parentId: null })),
    ...Object.entries(level2).flatMap(([parentId, children]) =>
      children.map(child => ({ ...child, level: "2", parentId }))
    )
  ];

  const filteredWarehouses = allWarehouses.filter(w =>

    (!filters.level || w.level === filters.level) &&

    (!filters.search ||
      w.warehouseCode.toLowerCase().includes(filters.search.toLowerCase()) ||
      w.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      w.location.toLowerCase().includes(filters.search.toLowerCase())
    ) &&
    
    (!filters.parentId || w.id === filters.parentId || w.parentId === filters.parentId) &&

    (!filters.minStorage || w.storageSize >= Number(filters.minStorage)) &&
    (!filters.maxStorage || w.storageSize <= Number(filters.maxStorage))
  );


  return (
    <div className="warehouses-page">

      {/* TOP SECTION */}
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>

      
      <div className="content-section">

        {/* Header */}
        <div className="warehouses-header">
          <div className="header-title-section">
            <h1>Manage warehouses</h1>
          </div>
          <div className="header-actions">
            <button className="back-btn" onClick={handleBackToDashboard} disabled={loading}>
              <ArrowLeft style={{ width: '16px', height: '16px' }} />
              Back to Dashboard
            </button>
            <button className="add-btn" onClick={() => { resetForm(); setShowModal(true); }} disabled={loading}>
              + Add Warehouse
            </button>
          </div>
        </div>

        {error && <div className="error-message">Error: {error}</div>}

        <div className="filters-section">
          <div className="form-group filter-group">
            <input
              type="text"
              placeholder=" "
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <label>Search warehouses...</label>
          </div>

          <div className="form-group filter-group">
            <select
              value={filters.parentId}
              onChange={(e) => setFilters({ ...filters, parentId: e.target.value })}
            >
              <option value="">All</option>
              {level1.map(parent => (
                <option key={parent.id} value={parent.id}>{parent.name}</option>
              ))}
            </select>
            <label>Parent warehouse</label>
          </div>

          <div className="form-group filter-group">
            <input
              type="number"
              placeholder=" "
              value={filters.minStorage}
              onChange={(e) => setFilters({ ...filters, minStorage: e.target.value })}
            />
            <label>Min storage</label>
          </div>

          <div className="form-group filter-group">
            <input
              type="number"
              placeholder=" "
              value={filters.maxStorage}
              onChange={(e) => setFilters({ ...filters, maxStorage: e.target.value })}
            />
            <label>Max storage</label>
          </div>

          <div className="form-group filter-group">
            <select
              value={filters.level}
              onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            >
              <option value="">All</option>
              <option value="1">Main (Level 1)</option>
              <option value="2">Child (Level 2)</option>
            </select>
            <label>Level</label>
          </div>
        </div>


        {/* WAREHOUSE TABLE */}
        <div className="warehouses-table-section">
          <h2>Warehouses in your system</h2>

          {loading && !warehouses.length ? (
            <div className="loading">Loading warehouses...</div>
          ) : (
            <table className="warehouses-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Storage</th>
                  <th>Level</th>
                  <th>Parent warehouse</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {level1.map(parent => {
                  // Check if parent passes filters
                  const parentFiltered = filteredWarehouses.some(
                    w => w.id === parent.id
                  );

                  // Get filtered children
                  const childrenFiltered = filteredWarehouses.filter(
                    w => w.level === "2" && w.parentId === parent.id
                  );

                  // Skip parent if neither it nor its children pass filters
                  if (!parentFiltered && childrenFiltered.length === 0) return null;

                  return (
                    <React.Fragment key={parent.id}>
                      {parentFiltered && (
                        <tr className="level1-row">
                          <td>{parent.warehouseCode}</td>
                          <td>{parent.name}</td>
                          <td>{parent.location}</td>
                          <td>{parent.storageSize}</td>
                          <td>Main (Level 1)</td>
                          <td>-</td>
                          <td>
                            <button className="edit-btn" onClick={() => handleEditWarehouse(parent)}>Edit</button>
                            <button className="remove-btn" onClick={() => handleRemoveWarehouse(parent.id)}>Remove</button>
                          </td>
                        </tr>
                      )}

                      {childrenFiltered.map(child => (
                        <tr key={child.id} className="level2-row">
                          <td>{child.warehouseCode}</td>
                          <td>{child.name}</td>
                          <td>{child.location}</td>
                          <td>{child.storageSize}</td>
                          <td>Child (Level 2)</td>
                          <td>{getWarehouseNameById(child.parentId)}</td>
                          <td>
                            <button className="edit-btn" onClick={() => handleEditWarehouse(child)}>Edit</button>
                            <button className="remove-btn" onClick={() => handleRemoveWarehouse(child.id)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>


        {/* MODAL */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>{editingWarehouse ? "Edit Warehouse" : "Create a New Warehouse"}</h3>

              <form onSubmit={handleCreateAndEdit} className="modal-form">

                <div className="form-group">
                  <input
                    type="text"
                    placeholder=" "
                    value={warehouseName}
                    onChange={(e) => setWarehouseName(e.target.value)}
                    required
                  />
                  <label>Warehouse name</label>
                </div>

                {/* Parent Warehouse Dropdown */}
                <div className="form-group">
                  <select
                    value={mainWarehouseId || ""}
                    onChange={(e) => setMainWarehouseId(e.target.value || null)}
                    className="floating-select"
                  >
                    <option value="">None (Main Warehouse)</option>
                    {level1.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                  <label>Parent Warehouse (optional)</label>
                </div>

                <label>Physical Address:</label>
                <div className="address-section">

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                    />
                    <label>Street name</label>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={suburb}
                      onChange={(e) => setSuburb(e.target.value)}
                    />
                    <label>Suburb</label>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                    <label>City</label>
                  </div>

                  <div className="form-group">
                    <input
                      type="number"
                      placeholder=" "
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                    />
                    <label>Postal code</label>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                    <label>Country</label>
                  </div>
                </div>

                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder=" "
                    value={storageSize}
                    onChange={(e) => setStorageSize(e.target.value)}
                    required
                  />
                  <label>Storage Size</label>
                  <p>m²</p>
                </div>

                <div className="modal-actions">
                  <button type="submit">{editingWarehouse ? "Save Changes" : "Create"}</button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => { setShowModal(false); resetForm(); }}
                  >
                    Cancel
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
